import {
  Building2,
  Wallet,
  IndianRupee,
  Landmark,
  Percent,
  ShieldCheck,
  Clock3,
  FileCheck2,
  Users,
  User,
  FileText,
  TrendingUp,
  CircleDollarSign,
  Banknote,
} from "./icons";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

/* -------------------------------------------------------------------------- */
/*                                    SEO                                     */
/* -------------------------------------------------------------------------- */

const schemas = [
  websiteSchema(),
  organizationSchema(),
  financialServiceSchema(),
  localBusinessSchema(),
  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Wealth Based Loan",
      path: "/wealth-based-loan",
    },
  ]),
];

export const seo = {
  title: "Wealth Based Loan | Loan Against Eligible Wealth | Money Mart",

  description:
    "Explore Wealth Based Loan options based on your eligible wealth and assets. Money Mart helps you compare financing options from leading Banks & NBFCs with expert guidance.",

  keywords:
    "Wealth Based Loan, Wealth Based Financing, Loan Against Wealth, Asset Based Loan, Wealth Loan, Loan Against Assets, Money Mart",

  canonical: "/wealth-based-loan",

  structuredData: {
    schemas,
  },
};

/* -------------------------------------------------------------------------- */
/*                                    Hero                                    */
/* -------------------------------------------------------------------------- */

export const hero = {
  badge: "Wealth Based Financing",

  title: "Unlock the Value of Your Wealth",

  description:
    "Access financing based on the value of your eligible wealth and assets. Money Mart helps you compare suitable lending options from leading Banks and NBFCs while keeping your financial profile and repayment capacity in focus.",

  highlights: [
    "Finance Based on Eligible Wealth",
    "Up to 60% of Wealth Value for Annual Repayment",
    "Equivalent to Up to 5% Monthly Repayment Capacity",
    "Expert Assistance from Enquiry to Disbursement",
  ],

  image: "/images/loans/wealth_based_loan.webp",

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

/* -------------------------------------------------------------------------- */
/*                                  Overview                                  */
/* -------------------------------------------------------------------------- */

export const overview = {
  badge: "Wealth Based Loan",

  title: "Turn Your Existing Wealth into Financing",

  description:
    "A Wealth Based Loan allows eligible borrowers to seek financing based on the value of their wealth and assets. Instead of relying only on regular income, the lender evaluates eligible wealth, ownership, valuation, liquidity, and overall repayment capacity.",

  items: [
    {
      icon: Wallet,
      title: "Finance Against Eligible Wealth",

      description:
        "Use eligible wealth and assets as the basis for evaluating your borrowing capacity, subject to lender policies and valuation.",
    },

    {
      icon: IndianRupee,
      title: "5% Monthly Repayment Capacity",

      description:
        "Based on the stated 60% annual repayment benchmark, up to 5% of eligible wealth value can be considered towards monthly loan repayment.",
    },

    {
      icon: ShieldCheck,
      title: "Preserve Your Financial Position",

      description:
        "Explore financing options based on your existing wealth without necessarily liquidating all your assets upfront.",
    },

    {
      icon: TrendingUp,
      title: "Designed for Wealth Holders",

      description:
        "Suitable for individuals with substantial eligible assets who want to unlock financing potential from their existing wealth.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                  Benefits                                  */
/* -------------------------------------------------------------------------- */

export const benefits = {
  badge: "Benefits",

  title: "Why Consider a Wealth Based Loan?",

  description:
    "A Wealth Based Loan can provide an alternative way to assess borrowing capacity for individuals whose financial strength is reflected significantly in their accumulated wealth and assets.",

  items: [
    {
      icon: CircleDollarSign,
      title: "Based on Eligible Wealth",

      description:
        "Borrowing capacity can be evaluated against the value of eligible wealth and assets, subject to lender assessment.",
    },

    {
      icon: Percent,
      title: "Up to 5% Monthly Repayment Capacity",

      description:
        "The stated 60% annual repayment benchmark translates to up to 5% of eligible wealth value towards monthly repayment.",
    },

    {
      icon: Wallet,
      title: "Unlock Existing Wealth",

      description:
        "Access financing potential from eligible assets instead of depending solely on regular monthly income.",
    },

    {
      icon: ShieldCheck,
      title: "Asset-Based Assessment",

      description:
        "Lenders can evaluate the type, ownership, value, liquidity, and other characteristics of eligible assets.",
    },

    {
      icon: Clock3,
      title: "Multiple Lending Options",

      description:
        "Money Mart helps compare available options across Banks and NBFCs based on your profile and requirements.",
    },

    {
      icon: Users,
      title: "Expert Loan Assistance",

      description:
        "Our advisors help you understand lender requirements, documentation, valuation, and available financing options.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

export const types = {
  badge: "Wealth Based Financing Options",

  title: "Financing Against Different Forms of Wealth",

  description:
    "Depending on lender policies, different forms of eligible wealth and assets may be considered when evaluating a Wealth Based Loan.",

  items: [
    {
      icon: Landmark,
      title: "Financial Investments",

      description:
        "Explore financing based on eligible financial investments such as securities or other approved investment holdings.",
    },

    {
      icon: Banknote,
      title: "Fixed Deposits & Deposits",

      description:
        "Eligible deposits may be considered by certain lenders based on their valuation and lending policies.",
    },

    {
      icon: Building2,
      title: "Property & Real Estate",

      description:
        "Property ownership and value may form part of the lender's assessment for suitable asset-backed financing options.",
    },

    {
      icon: TrendingUp,
      title: "Investment Portfolio",

      description:
        "An eligible investment portfolio may be evaluated to determine the borrowing potential available to the applicant.",
    },

    {
      icon: Wallet,
      title: "Other Eligible Assets",

      description:
        "Other forms of documented and acceptable wealth may be considered depending on the lender's policy.",
    },

    {
      icon: CircleDollarSign,
      title: "Consolidated Wealth",

      description:
        "Where permitted, multiple eligible assets may be evaluated together to understand the applicant's overall financial strength.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                 Eligibility                                */
/* -------------------------------------------------------------------------- */

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your Wealth Based Loan Eligibility",

  description:
    "Eligibility depends on the nature and value of your wealth, ownership, liquidity, documentation, credit profile, and the lender's assessment criteria.",

  requirements: [
    {
      icon: IndianRupee,
      title: "Eligible Wealth",

      value:
        "Adequate eligible wealth or assets as accepted by the lender",
    },

    {
      icon: FileCheck2,
      title: "Ownership & Documentation",

      value:
        "Clear ownership and supporting documents for the assets being considered",
    },

    {
      icon: TrendingUp,
      title: "Asset Value",

      value:
        "Wealth value must meet the lender's valuation and lending criteria",
    },

    {
      icon: User,
      title: "Credit Profile",

      value:
        "Healthy credit history and satisfactory repayment profile preferred",
    },
  ],

  approvalFactors: [
    "Value and type of eligible wealth",
    "Ownership and supporting asset documents",
    "Liquidity and marketability of the assets",
    "Applicant's credit score and repayment history",
    "Existing loans and financial liabilities",
    "Overall repayment capacity",
    "Lender-specific valuation and eligibility policies",
  ],

  cta: {
    title: "Not sure if your wealth qualifies?",

    description:
      "Share your financial profile with our advisors. We will help assess the available options and compare suitable lenders based on your requirements.",

    buttonText: "Check Eligibility Now",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Documents                                 */
/* -------------------------------------------------------------------------- */

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for a Wealth Based Loan",

  description:
    "The exact documentation depends on the type of wealth or assets being considered and the lender's requirements. Common documents may include the following.",

  groups: [
    {
      icon: Users,
      title: "KYC Documents",

      documents: [
        "PAN Card",
        "Latest Downloaded Aadhaar Card",
        "Passport-size Photograph / Selfie",
        "Address Proof, if required",
      ],
    },

    {
      icon: Wallet,
      title: "Wealth & Asset Documents",

      documents: [
        "Documents establishing ownership of eligible assets",
        "Investment / holding statements, where applicable",
        "Fixed Deposit or deposit certificates, where applicable",
        "Property ownership and valuation documents, where applicable",
      ],
    },

    {
      icon: IndianRupee,
      title: "Banking & Financial Documents",

      documents: [
        "Latest Bank Account Statements",
        "Income documents, where required",
        "Existing Loan Details, if applicable",
        "Investment or financial statements, where applicable",
      ],
    },

    {
      icon: Building2,
      title: "Property Documents",

      documents: [
        "Complete Chain of Property Documents, where applicable",
        "Property Valuation Report, if required",
        "Latest Property Tax Receipt, where applicable",
        "Approved Building Plan / Permissions, where applicable",
      ],
    },

    {
      icon: FileText,
      title: "Additional Documents",

      documents: [
        "Income Tax Returns, where applicable",
        "CA-certified financial documents, where required",
        "Asset valuation reports, where required",
        "Any additional documents requested by the lender",
      ],
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Process                                  */
/* -------------------------------------------------------------------------- */

export const loanProcess = {
  ...process,

  title: "Your Wealth Based Loan in 6 Simple Steps",

  description:
    "From understanding your wealth profile to loan disbursement, our advisors help you navigate lender selection, documentation, valuation, and approval.",
};

/* -------------------------------------------------------------------------- */
/*                               Why Money Mart                               */
/* -------------------------------------------------------------------------- */

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Explore a Wealth Based Loan Through Money Mart?",

  description:
    "We help you understand your financing potential, compare available Banks and NBFCs, and navigate lender-specific eligibility and documentation requirements.",

  items: whyMoneyMartItems,
};

/* -------------------------------------------------------------------------- */
/*                                     FAQ                                    */
/* -------------------------------------------------------------------------- */

export const faq = {
  badge: "Frequently Asked Questions",

  title: "Wealth Based Loan FAQs",

  description:
    "Find answers to common questions about Wealth Based Loans and asset-based financing.",

  items: [
    {
      question: "What is a Wealth Based Loan?",

      answer:
        "A Wealth Based Loan is a financing option where the lender evaluates the applicant's eligible wealth and assets as an important part of determining borrowing capacity, subject to lender policies.",
    },

    {
      question: "How much of my wealth can be considered for repayment?",

      answer:
        "Under the repayment benchmark used for this program, up to 60% of eligible wealth value can be considered towards annual repayment. This is equivalent to up to 5% of the eligible wealth value towards monthly repayment.",
    },

    {
      question: "What types of wealth can be considered?",

      answer:
        "The assets considered depend on the lender. Eligible financial investments, deposits, property, and other documented assets may be considered where permitted by the lender's policy.",
    },

    {
      question: "Do I need a regular monthly income?",

      answer:
        "A lender may consider wealth and assets as a significant part of the assessment, but income, credit profile, liabilities, liquidity, and other factors may still be evaluated depending on the lender.",
    },

    {
      question: "Will my assets need to be sold to get the loan?",

      answer:
        "Not necessarily. The purpose of this financing structure is to evaluate eligible wealth as part of the borrowing assessment. The exact structure and whether an asset must be pledged or otherwise secured depends on the lender and product.",
    },

    {
      question: "How much loan can I get?",

      answer:
        "The eligible loan amount depends on the type and value of eligible wealth, lender valuation, repayment capacity, credit profile, existing liabilities, and applicable lender policies.",
    },

    {
      question: "What documents are required?",

      answer:
        "You will generally need KYC documents and documents establishing ownership and value of the wealth or assets being considered. Additional financial, banking, property, or valuation documents may be required depending on the lender.",
    },

    {
      question: "Can Money Mart compare different lenders?",

      answer:
        "Yes. Money Mart can help you understand available options from participating Banks and NBFCs and identify lenders whose eligibility criteria are more suitable for your profile.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                     CTA                                    */
/* -------------------------------------------------------------------------- */

export const cta = {
  badge: "Ready to Unlock Your Wealth?",

  title: "Let's Explore the Right Wealth Based Financing Option",

  description:
    "Understand your financing potential, compare suitable Banks and NBFCs, and get expert assistance throughout the loan process.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for Wealth Based Loan",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Lead Form                                 */
/* -------------------------------------------------------------------------- */

export const leadForm = {
  badge: "Free Wealth Based Loan Consultation",

  title: "Speak to a Wealth Based Loan Expert",

  description:
    "Share your details and our advisors will understand your wealth profile, financing requirement, and help identify suitable lending options.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our Wealth Based Loan expert will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Employment Type",
      income: "Monthly Income",
      loanAmount: "Required Loan Amount",
    },

    incomeField: "monthlyIncome",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-50k",
        label: "Below ₹50,000",
      },
      {
        value: "50k-1l",
        label: "₹50,000 – ₹1 Lakh",
      },
      {
        value: "1-2l",
        label: "₹1 Lakh – ₹2 Lakhs",
      },
      {
        value: "2-5l",
        label: "₹2 Lakhs – ₹5 Lakhs",
      },
      {
        value: "above-5l",
        label: "Above ₹5 Lakhs",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your Wealth Based Loan enquiry. No spam. No hidden charges.",
  },
};

/* -------------------------------------------------------------------------- */
/*                                Final Export                                */
/* -------------------------------------------------------------------------- */

const wealthBasedLoan = {
  seo,
  hero,
  overview,
  benefits,
  types,
  eligibility,
  documents,
  process: loanProcess,
  whyMoneyMart,
  faq,
  cta,
  leadForm,
};

export default wealthBasedLoan;